/* One chapter on the map.
 *
 * The card carries the block map: one pip per step, where the shape encodes the
 * kind and the fill encodes whether it is done. Each pip is a real target that
 * opens the chapter and lands on that step — so the map is also the table of
 * contents. Its accessible name spells out the chapter, its state, and the
 * prerequisite when it is still locked.
 */
import { useTranslation } from 'react-i18next';
import {
  blockDone,
  byId,
  curBlock,
  firstOpen,
  pad,
  parents,
  progress as chapterProgress,
  stateOf,
  xpOf,
} from '../../engine/progress';
import { useApp } from '../../state/AppProvider';
import type { NodeDef } from '../../types';

interface GraphNodeProps {
  node: NodeDef;
  onOpen: (id: string) => void;
}

export function GraphNode({ node, onOpen }: GraphNodeProps) {
  const { t } = useTranslation();
  const { progress, sel, ignite, goBlock } = useApp();

  const state = stateOf(node, progress);
  const next = firstOpen(progress);
  const isNext = !!next && next.id === node.id && state === 'available';
  const locked = state === 'locked';
  const parent = locked ? byId(parents(node.id)[0]) : null;
  const stateWord = locked ? t('routes:legendLater') : state === 'available' ? t('legendAvailable') : t('legendDone');
  const current = curBlock(node, progress);

  const label =
    `${pad(node)} · ${t(node.titleKey)} · ${stateWord}` + (parent ? ` · ${t('after')} ${pad(parent)}` : '');

  return (
    <div
      className={`node${sel === node.id ? ' is-active' : ''}`}
      data-id={node.id}
      data-state={state}
      data-extra={node.extra ? 'true' : 'false'}
      data-bonus={node.bonus ? 'true' : 'false'}
      data-next={isNext ? 'true' : undefined}
      data-ignite={ignite === node.id ? 'true' : undefined}
      data-od-id={`node-${node.id}`}
      style={{ left: node.x, top: node.y, width: node.w, height: node.h }}
    >
      <button
        type="button"
        className="node-open"
        data-act="node"
        data-id={node.id}
        aria-label={label}
        onClick={() => onOpen(node.id)}
      />
      <span className="node-line">
        <span className="node-tag">
          {node.bonus ? t('legendBonus') : node.extra ? t('optional') : t(node.tagKey)}
        </span>
        <span className="node-idx num">{pad(node)}</span>
      </span>
      <span className="node-title">{t(node.titleKey)}</span>
      <span className="node-sub">{t(node.subKey)}</span>
      <span className="node-foot">
        {locked ? (
          <span>
            {t('after')} {pad(parent)}
          </span>
        ) : (
          <span className="xp">+{xpOf(node)} XP</span>
        )}
        <span className="blockmap" data-od-id={`blockmap-${node.id}`}>
          {node.blocks.map((block, index) => {
            const done = blockDone(block, progress);
            const pipState = done ? 'done' : sel === node.id && current === index ? 'cur' : 'todo';
            const word = done ? t('legendDone') : pipState === 'cur' ? t('legendAvailable') : t('legendLocked');
            return (
              <button
                key={block.id}
                type="button"
                className="pip"
                tabIndex={-1}
                data-act="jump"
                data-id={node.id}
                data-i={index}
                data-kind={block.kind}
                data-s={pipState}
                aria-label={`${pad(node)} · ${t('blockOf')} ${('0' + (index + 1)).slice(-2)} · ${t(`kind.${block.kind}`)} · ${t(block.titleKey)} · ${word}`}
                onClick={() => {
                  if (locked) return;
                  onOpen(node.id);
                  goBlock(node.id, index);
                }}
              />
            );
          })}
        </span>
      </span>
      <span className="node-bar">
        <i style={{ width: `${Math.round((chapterProgress(node, progress) / node.blocks.length) * 100)}%` }} />
      </span>
    </div>
  );
}
